import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, Search, Settings, Sun, Moon, Lock, Trash2, BookOpen, FilePlus, Upload } from 'lucide-react';
import { type Book } from '../../types';
import { APISettingsModal } from '../../components/modals/APISettingsModal';
import { TrashPanel } from '../../components/TrashPanel';
import { PasswordInputModal } from '../../components/modals/PasswordInputModal';
import { SuccessModal } from '../../components/modals/SuccessModal';
import { ConfirmImportDuplicateModal } from '../../components/modals/ConfirmImportDuplicateModal';
import { RenameImportBookModal } from '../../components/modals/RenameImportBookModal';
import { BatchImportManager } from '../../components/BatchImportManager';
import { EditBookModal } from './components/EditBookModal';
import { LoadError } from '../../components/LoadError';
import { ConfirmDeleteModal } from './components/ConfirmDeleteModal';
import { BookCard } from './components/BookCard';
import { HomeStats } from './components/HomeStats';
import { useExportImport } from './useExportImport';
import { exportBookAsText, exportBookAsDocx, EXPORT_PRESETS, DEFAULT_PRESET } from '../../utils/exportFormats';
import { notifyError, notifySuccess, notifyWarn, errorMessage } from '../../utils/notify';
import { dataCache } from '../../cache/DataCache';
import { isDesktopHost } from '../../utils/host';

export function Home() {
  const [books, setBooks] = useState<Book[]>(() => dataCache.books ?? []);
  const [deletedBooks, setDeletedBooks] = useState<Book[]>(() => dataCache.deletedBooks ?? []);
  /** 书架加载失败信息：与"确实没有作品"必须区分开 */
  const [booksError, setBooksError] = useState<string | null>(null);
  /** 文本导出预设（记忆在服务端偏好，跨设备一致） */
  const [exportPresetId, setExportPresetId] = useState<string>('default');
  const [editingBook, setEditingBook] = useState<Book | null>(null);
  const [isCreatingBook, setIsCreatingBook] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingBook, setDeletingBook] = useState<Book | null>(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showTrashPanel, setShowTrashPanel] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const stored = localStorage.getItem('dark-mode');
    const isDark = stored !== null ? stored === '1' : document.documentElement.classList.contains('dark-mode');
    if (isDark) document.documentElement.classList.add('dark-mode');
    else document.documentElement.classList.remove('dark-mode');
    return isDark;
  });
  const [isEntering, setIsEntering] = useState(true);
  const [isExiting, setIsExiting] = useState(false);
  const [newlyImportedBookId, setNewlyImportedBookId] = useState<string | null>(null);
  const [appVersion, setAppVersion] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const [bookSort, setBookSort] = useState<'created' | 'recent'>(() => (localStorage.getItem('books-sort') === 'recent' ? 'recent' : 'created'));

  const navigate = useNavigate();
  const location = useLocation();
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const breatheTimerRef = useRef<NodeJS.Timeout | null>(null);

  const exportImport = useExportImport({
    books,
    loadBooks: async () => { await loadBooks(); },
    setNewlyImportedBookId,
    breatheTimerRef,
  });

  useEffect(() => {
    setIsEntering(true);
    setIsExiting(false);
    const timer = setTimeout(() => setIsEntering(false), 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (location.pathname === '/home') {
      if (timerRef.current) clearTimeout(timerRef.current);
      setIsEntering(true);
      setIsExiting(false);
      const newTimer = setTimeout(() => {
        setIsEntering(false);
        timerRef.current = null;
      }, 300);
      timerRef.current = newTimer;
    }
  }, [location.pathname]);

  const loadBooks = async () => {
    try {
      const data = await window.api.getBooks({ sort: bookSort });
      dataCache.books = data;
      setBooks(data);
      setBooksError(null);
    } catch (err) {
      // 失败必须可见：否则书架会落到"空空如也"分支，用户以为作品丢了
      setBooksError(errorMessage(err));
    }
  };

  // 排序方式切换：重新拉取并按最近编辑/创建时间排序
  useEffect(() => {
    localStorage.setItem('books-sort', bookSort);
    void loadBooks();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookSort]);

  const loadDeletedBooks = async () => {
    try {
      const data = await window.api.getDeletedBooks();
      dataCache.deletedBooks = data;
      setDeletedBooks(data);
    } catch (err) {
      notifyError('回收站加载失败：' + errorMessage(err));
    }
  };

  useEffect(() => {
    void loadBooks();
    void loadDeletedBooks();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window.api as any).getAppVersion?.().then((v: string) => setAppVersion(v || '')).catch(() => { /* 版本号缺失不影响使用 */ });
  }, []);

  // 读取已保存的导出预设（与其它偏好同一存储，跨设备一致）
  useEffect(() => {
    let cancelled = false;
    window.api.getUserPreference('EXPORT_PRESET')
      .then((pref) => {
        const value = pref?.preference_value;
        if (!cancelled && value && EXPORT_PRESETS[value]) setExportPresetId(value);
      })
      .catch(() => { /* 无偏好时用默认预设 */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (showTrashPanel) loadDeletedBooks();
  }, [showTrashPanel]);

  // 关闭创建菜单（点击外部时关闭）
  useEffect(() => {
    if (!createMenuOpen) return;
    const close = () => setCreateMenuOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [createMenuOpen]);

  useEffect(() => {
    if (isDarkMode) document.documentElement.classList.add('dark-mode');
    else document.documentElement.classList.remove('dark-mode');
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    const nextMode = !isDarkMode;
    setIsDarkMode(nextMode);
    if (nextMode) document.documentElement.classList.add('dark-mode');
    else document.documentElement.classList.remove('dark-mode');
    localStorage.setItem('dark-mode', nextMode ? '1' : '0');
  };

  const handleCreateBook = async (data: { title: string; description: string; cover: string }) => {
    const finalCover = data.cover.trim() === '' ? '' : data.cover;
    try {
      await window.api.createBook({ ...data, cover: finalCover });
      setIsCreatingBook(false);
      void loadBooks();
    } catch (err) {
      // 此前没有 catch：失败会被吞成未处理的 Promise rejection，界面毫无反馈
      notifyError('创建作品失败：' + errorMessage(err));
    }
  };

  const handleUpdateBook = async (data: { id: string; title: string; description: string; cover: string }) => {
    try {
      await window.api.updateBook(data);
      setEditingBook(null);
      void loadBooks();
    } catch (err) {
      notifyError('保存作品信息失败：' + errorMessage(err));
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingBook) return;
    try {
      await window.api.deleteBooks([deletingBook.id]);
      setDeletingBook(null);
      setShowDeleteModal(false);
      void loadBooks();
      void loadDeletedBooks();
    } catch (err) {
      notifyError('删除作品失败：' + errorMessage(err));
    }
  };

  const handleRestoreBook = async (ids: string[]) => {
    await window.api.restoreBooks(ids);
    loadBooks();
    loadDeletedBooks();
  };

  const handlePermanentDeleteBooks = async () => {
    loadBooks();
    loadDeletedBooks();
  };

  const handleCloseTrashPanel = () => {
    setShowTrashPanel(false);
    loadBooks();
    loadDeletedBooks();
  };

  const handleNavigateToEditor = (bookId: string) => {
    setIsExiting(true);
    setTimeout(() => {
      navigate(`/editor/${bookId}`);
    }, 300);
  };

  const handleLock = async () => {
    sessionStorage.removeItem('sw_authenticated');
    await window.api.logoutUser();
    navigate('/');
  };

  const handleExportBook = (book: Book) => {
    exportImport.handleExportBook([book.id]);
  };

  const handleExportText = async (book: Book, format: 'txt' | 'md', encoding: 'utf8' | 'gbk' = 'utf8') => {
    const preset = EXPORT_PRESETS[exportPresetId]?.preset ?? DEFAULT_PRESET;
    const result = await exportBookAsText(book, format, { encoding, preset });
    if (!result.success) {
      notifyError(`导出${format === 'txt' ? 'TXT' : 'Markdown'}失败：${result.error}`);
      return;
    }
    // GBK 无法表示的字符会降级为字符引用，必须告知用户而非静默替换
    if (result.replacedCount && result.replacedCount > 0) {
      const samples = result.replacedSamples?.join(' ') ?? '';
      notifyWarn(`有 ${result.replacedCount} 个字符无法用 GBK 表示，已转为字符引用${samples ? `（如 ${samples}）` : ''}。建议改用 UTF-8 导出。`);
    } else {
      notifySuccess(`已导出 ${format === 'txt' ? 'TXT' : 'Markdown'}${encoding === 'gbk' ? '（GBK）' : ''}`);
    }
  };

  const handleExportDocx = async (book: Book) => {
    const result = await exportBookAsDocx(book);
    if (!result.success) notifyError(`导出 Word 失败：${result.error}`);
  };

  const handleRead = (book: Book) => {
    navigate(`/reader/${book.id}`);
  };

  const handleRequestDelete = (book: Book) => {
    setDeletingBook(book);
    setShowDeleteModal(true);
  };

  const filteredBooks = searchQuery
    ? books.filter(b => b.title.toLowerCase().includes(searchQuery.toLowerCase()))
    : books;

  const trashCount = deletedBooks.length;

  return (
    <div className="home-container" style={{ animation: isEntering ? 'fadeUp 0.3s ease-out both' : (isExiting ? 'fadeOut 0.2s ease-in forwards' : 'none') }}>
      <div className="home-page">
        <header className="home-header">
          <div className="home-header-left">
            <h1 className="home-title">我的书架</h1>
            <div className="home-search">
              <Search size={16} className="home-search-icon" />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜索作品..."
                className="home-search-input"
              />
            </div>
            <select
              value={bookSort}
              onChange={(e) => setBookSort(e.target.value as 'created' | 'recent')}
              className="home-sort-select"
              title="书架排序"
            >
              <option value="created">按创建时间</option>
              <option value="recent">按最近编辑</option>
            </select>
          </div>
          <div className="home-header-right">
            {/* 新建作品按钮 + 下拉菜单 */}
            <div className="home-create-wrapper">
              <button
                className="home-create-btn"
                onClick={(e) => { e.stopPropagation(); setCreateMenuOpen(prev => !prev); }}
              >
                <Plus size={16} />
                <span>新建作品</span>
              </button>
              {createMenuOpen && (
                <div className="home-create-menu" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => { setCreateMenuOpen(false); setIsCreatingBook(true); }}>
                    <FilePlus size={15} />
                    <span>新建空白作品</span>
                  </button>
                  <button onClick={() => { setCreateMenuOpen(false); exportImport.handleImportBook(); }}>
                    <Upload size={15} />
                    <span>从本地导入</span>
                  </button>
                </div>
              )}
            </div>

            <button className="home-header-icon-btn" onClick={() => setShowTrashPanel(true)} title="回收站">
              <Trash2 size={18} />
              {trashCount > 0 && <span className="home-header-badge">{trashCount > 99 ? '99+' : trashCount}</span>}
            </button>

            <div className="home-header-separator" />

            <button className="home-header-icon-btn" onClick={() => setShowSettingsModal(true)} title="API 设置">
              <Settings size={18} />
            </button>
            <button className="home-header-icon-btn" onClick={toggleDarkMode} title={isDarkMode ? '日间模式' : '夜间模式'}>
              {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button className="home-header-icon-btn" onClick={handleLock} title="锁定">
              <Lock size={18} />
            </button>
            {/* 文本导出预设：影响 TXT/Markdown 的标题写法与是否含摘要 */}
            <select
              className="home-export-preset"
              value={exportPresetId}
              title="文本导出预设（TXT / Markdown）"
              aria-label="文本导出预设"
              onChange={(e) => {
                const next = e.target.value;
                setExportPresetId(next);
                void window.api.setUserPreference('EXPORT_PRESET', next).catch(() => { /* 保存失败下次启动回落默认 */ });
              }}
            >
              {Object.entries(EXPORT_PRESETS).map(([id, p]) => (
                <option key={id} value={id}>{p.label}</option>
              ))}
            </select>
          </div>
        </header>

        <HomeStats />

        <div className="home-content">
          {booksError ? (
            /* 加载失败不再伪装成"书架空空如也"——那会让用户以为作品丢了 */
            <LoadError message={`书架加载失败：${booksError}`} onRetry={() => { void loadBooks(); }} />
          ) : filteredBooks.length > 0 ? (
            <main className="book-grid">
              {filteredBooks.map(book => (
                <BookCard
                  key={book.id}
                  book={book}
                  isNewlyImported={newlyImportedBookId === book.id}
                  onClick={() => handleNavigateToEditor(book.id)}
                  onEdit={setEditingBook}
                  onExport={handleExportBook}
                  onExportText={handleExportText}
                  onExportDocx={handleExportDocx}
                  onRead={handleRead}
                  onDelete={handleRequestDelete}
                />
              ))}
            </main>
          ) : (
            <div className="home-empty">
              <div className="home-empty-icon">
                <BookOpen size={48} strokeWidth={1} />
              </div>
              <h2 className="home-empty-title">
                {searchQuery ? '没有找到匹配的作品' : '书架空空如也'}
              </h2>
              <p className="home-empty-desc">
                {searchQuery ? '试着换一个关键词吧' : '创建你的第一部作品，开始书写属于你的故事'}
              </p>
              {!searchQuery && (
                <button className="home-create-btn" onClick={() => setIsCreatingBook(true)}>
                  <Plus size={16} />
                  <span>创建作品</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 桌面端在版本号前标出来源：出问题时用户能一句话说清"我是在哪个端上遇到的" */}
      <div className="home-version">{isDesktopHost() ? `桌面版 v${appVersion}` : `v${appVersion}`}</div>

      {isCreatingBook && <EditBookModal book={{ id: '', title: '', description: '', cover: '', created_at: new Date().toISOString(), updated_at: new Date().toISOString(), word_count: 0, user_id: '', is_active: true }} isCreating={true} onClose={() => setIsCreatingBook(false)} onSave={handleCreateBook} />}
      {editingBook && <EditBookModal book={editingBook} isCreating={false} onClose={() => setEditingBook(null)} onSave={handleUpdateBook} />}
      {showDeleteModal && deletingBook && <ConfirmDeleteModal count={1} onClose={() => { setShowDeleteModal(false); setDeletingBook(null); }} onConfirm={handleDeleteConfirm} />}
      {showSettingsModal && <APISettingsModal onClose={() => setShowSettingsModal(false)} />}
      <TrashPanel isOpen={showTrashPanel} deletedBooks={deletedBooks} onClose={handleCloseTrashPanel} onRestore={handleRestoreBook} onPermanentDelete={handlePermanentDeleteBooks} />
      {exportImport.showPasswordModal && exportImport.passwordModalCallback && <PasswordInputModal title={exportImport.passwordModalTitle} message={exportImport.passwordModalMessage} error={exportImport.passwordModalError} onConfirm={(password) => { exportImport.passwordModalCallback!(password); exportImport.setPasswordModalError(''); }} onCancel={() => { exportImport.setShowPasswordModal(false); exportImport.setPasswordModalCallback(null); exportImport.setPasswordModalError(''); }} />}
      {exportImport.showSuccessModal && <SuccessModal title={exportImport.successTitle} message={exportImport.successMessage} showButton={true} buttonText="确认" autoClose={false} onClose={exportImport.handleSuccessModalClose} />}
      {exportImport.showDuplicateConfirm && <ConfirmImportDuplicateModal bookTitle={exportImport.duplicateBookTitle} onConfirm={exportImport.handleConfirmImportDuplicate} onCancel={exportImport.handleCancelImportDuplicate} />}
      {exportImport.showRenameImport && <RenameImportBookModal originalTitle={exportImport.renameImportOriginalTitle} existingTitles={books.map(b => b.title)} onConfirm={exportImport.handleRenameImportConfirm} onCancel={exportImport.handleRenameImportCancel} />}
      {exportImport.showBatchImportManager && <BatchImportManager books={exportImport.batchImportBooks} existingTitles={books.map(b => b.title)} onConfirm={exportImport.handleBatchImportConfirm} onCancel={exportImport.handleBatchImportCancel} />}
    </div>
  );
}
